import { ReactElement, useEffect, useRef, useState } from 'react';
import { Overlay, Popover } from 'react-bootstrap';
import { BILLING_CORRECTION_JIRA_URL } from './billing-constants';

interface BillingCorrectionTooltipProps {
  id: string;
  message: string;
  children: ReactElement;
}

const HIDE_DELAY_MS = 200;

// Hover popover that stays open while hovered so the correction-request link is clickable.
// Built on Overlay/Popover (not OverlayTrigger/Tooltip) because it must be interactive.
export const BillingCorrectionTooltip = ({
  id,
  message,
  children
}: BillingCorrectionTooltipProps) => {
  const [anchor, setAnchor] = useState<HTMLSpanElement | null>(null);
  const [open, setOpen] = useState(false);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const show = () => {
    clearTimeout(hideTimer.current);
    setOpen(true);
  };
  // Delay hiding so the pointer can cross the gap between the trigger and the popover.
  const hideSoon = () => {
    hideTimer.current = setTimeout(() => setOpen(false), HIDE_DELAY_MS);
  };

  useEffect(() => () => clearTimeout(hideTimer.current), []);

  return (
    <>
      <span
        ref={setAnchor}
        className='billing-correction-anchor'
        onMouseEnter={show}
        onMouseLeave={hideSoon}
      >
        {children}
      </span>
      <Overlay target={anchor} show={open} placement='top'>
        <Popover id={id} onMouseEnter={show} onMouseLeave={hideSoon}>
          <Popover.Body>
            {message} Need a change?{' '}
            <a href={BILLING_CORRECTION_JIRA_URL} target='_blank' rel='noreferrer'>
              Submit a billing correction request
            </a>
            .
          </Popover.Body>
        </Popover>
      </Overlay>
    </>
  );
};
